import React, { useEffect, useMemo, useState } from 'react';
import './HomeDecor.scss';
import { CATEGORY_ICON, DecorGlyph, Glyph } from './icons';

interface CatalogItem {
  id: number;
  name: string;
  category: string;
  price: number;
}

interface Category {
  id: string;
  label: string;
}

interface OwnedItem {
  refrId: number;
  name: string;
  category: string;
  price: number;
  sellFor: number;
  // Distance to the occupant, in metres
  d: number;
}

interface OriginalMark {
  refId: number;
  name: string;
}

interface NearbyObject {
  refId: number;
  name: string;
  d: number;
  hidden: boolean;
  moved: boolean;
}

interface DecorState {
  houseName: string;
  // false for a commerce: its original furniture stays, it can only be moved
  canRemoveOriginals?: boolean;
  money: number;
  maxItems: number;
  resalePercent: number;
  items: OwnedItem[];
  hidden: OriginalMark[];
  moved: OriginalMark[];
  categories?: Category[];
  catalog?: CatalogItem[];
}

type Tab = 'shop' | 'mine' | 'originals';
type Sort = 'price-asc' | 'price-desc' | 'name';

const send = (payload: Record<string, unknown>): void => {
  if ((window as any).skyrimPlatform?.sendMessage) {
    (window as any).skyrimPlatform.sendMessage(JSON.stringify({ source: 'home-decor', ...payload }));
  }
};

const norm = (s: string): string => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const fmt = (n: number): string => n.toLocaleString('fr-FR');

const HomeDecor: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [state, setState] = useState<DecorState | null>(null);
  const [catalog, setCatalog] = useState<CatalogItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [nearby, setNearby] = useState<NearbyObject[] | null>(null);
  const [tab, setTab] = useState<Tab>('shop');
  const [category, setCategory] = useState('all');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<Sort>('price-asc');

  useEffect(() => {
    const onState = (e: Event) => {
      const detail = (e as CustomEvent).detail as DecorState;
      if (detail.catalog) setCatalog(detail.catalog);
      if (detail.categories) setCategories(detail.categories);
      setState(detail);
      setVisible(true);
    };
    const onNearby = (e: Event) => setNearby(((e as CustomEvent).detail?.objects ?? []) as NearbyObject[]);
    const onClose = () => setVisible(false);
    window.addEventListener('skymp5-client:homeDecorState', onState);
    window.addEventListener('skymp5-client:homeDecorNearby', onNearby);
    window.addEventListener('skymp5-client:homeDecorClose', onClose);
    return () => {
      window.removeEventListener('skymp5-client:homeDecorState', onState);
      window.removeEventListener('skymp5-client:homeDecorNearby', onNearby);
      window.removeEventListener('skymp5-client:homeDecorClose', onClose);
    };
  }, []);

  const close = () => {
    setVisible(false);
    send({ action: 'close' });
  };

  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible]);

  // The list of the house's own contents is asked for when its tab opens
  useEffect(() => {
    if (visible && tab === 'originals') send({ action: 'scan' });
  }, [visible, tab]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: catalog.length };
    for (const i of catalog) c[i.category] = (c[i.category] ?? 0) + 1;
    return c;
  }, [catalog]);

  const shown = useMemo(() => {
    const q = norm(query.trim());
    const list = catalog.filter((i) => (category === 'all' || i.category === category) && (!q || norm(i.name).includes(q)));
    const sorted = list.slice();
    if (sort === 'price-asc') sorted.sort((a, b) => a.price - b.price || a.name.localeCompare(b.name));
    else if (sort === 'price-desc') sorted.sort((a, b) => b.price - a.price || a.name.localeCompare(b.name));
    else sorted.sort((a, b) => a.name.localeCompare(b.name));
    return sorted;
  }, [catalog, category, query, sort]);

  if (!visible || !state) return null;

  const full = state.items.length >= state.maxItems;
  const fill = Math.min(100, Math.round((state.items.length / Math.max(1, state.maxItems)) * 100));
  const categoryLabel = (id: string): string => categories.find((c) => c.id === id)?.label ?? '';
  const marks = [
    ...state.hidden.map((h) => ({ ...h, kind: 'retiré' })),
    ...state.moved.map((m) => ({ ...m, kind: 'déplacé' })),
  ];

  const tabs: Array<{ id: Tab; label: string; glyph: string; badge?: number }> = [
    { id: 'shop', label: 'Boutique', glyph: 'chest' },
    { id: 'mine', label: 'Mes meubles', glyph: 'chair', badge: state.items.length },
    { id: 'originals', label: 'Objets d\'origine', glyph: 'crate', badge: marks.length || undefined },
  ];

  return (
    <div className="home-decor">
      <div className="home-decor__window">
        <header className="home-decor__header">
          <div className="home-decor__heading">
            <h2 className="home-decor__title">Aménager le logement</h2>
            <p className="home-decor__sub">{state.houseName}</p>
          </div>
          <div className="home-decor__stats">
            <div className="home-decor__stat" title="Septims en poche">
              <span className="home-decor__stat-icon"><Glyph id="coin" /></span>
              <span className="home-decor__stat-value">{fmt(state.money)}</span>
            </div>
            <div className="home-decor__stat home-decor__stat--cap" title={`${state.items.length} meubles sur ${state.maxItems}`}>
              <span className="home-decor__stat-icon"><Glyph id="chair" /></span>
              <span className="home-decor__stat-value">{state.items.length} / {state.maxItems}</span>
              <span className="home-decor__meter" aria-hidden="true"><span style={{ width: `${fill}%` }} /></span>
            </div>
          </div>
        </header>

        <nav className="home-decor__tabs" role="tablist">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={`home-decor__tab${tab === t.id ? ' is-active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              <span className="home-decor__tab-icon"><Glyph id={t.glyph} /></span>
              {t.label}
              {t.badge ? <span className="home-decor__count">{t.badge}</span> : null}
            </button>
          ))}
        </nav>

        <div className="home-decor__body" key={tab}>
          {tab === 'shop' && (
            <div className="home-decor__shop">
              <aside className="home-decor__cats">
                {[{ id: 'all', label: 'Tout' }, ...categories].map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className={`home-decor__cat${category === c.id ? ' is-active' : ''}`}
                    onClick={() => setCategory(c.id)}
                  >
                    <span className="home-decor__cat-icon"><Glyph id={CATEGORY_ICON(c.id)} /></span>
                    <span className="home-decor__cat-label">{c.label}</span>
                    <span className="home-decor__cat-count">{counts[c.id] ?? 0}</span>
                  </button>
                ))}
              </aside>

              <section className="home-decor__catalog">
                <div className="home-decor__toolbar">
                  <label className="home-decor__search">
                    <span className="home-decor__search-icon"><Glyph id="search" /></span>
                    <input
                      type="text"
                      placeholder="Chercher un meuble"
                      value={query}
                      onChange={(e) => setQuery(e.target.value.slice(0, 40))}
                    />
                  </label>
                  <select className="home-decor__select" value={sort} onChange={(e) => setSort(e.target.value as Sort)} aria-label="Trier">
                    <option value="price-asc">Prix croissant</option>
                    <option value="price-desc">Prix décroissant</option>
                    <option value="name">Ordre alphabétique</option>
                  </select>
                </div>

                <p className="home-decor__note">
                  Clique sur Placer : le meuble apparaît devant toi, tu le places à ta guise, et tu ne paies qu'en validant avec Entrée.
                  {full ? ' Ton logement est plein : range un meuble pour en poser un autre.' : ''}
                </p>

                {shown.length === 0 ? (
                  <div className="home-decor__empty">
                    <span className="home-decor__empty-icon"><Glyph id="search" /></span>
                    <p>Aucun meuble ne correspond.</p>
                    <button type="button" className="home-decor__button" onClick={() => { setQuery(''); setCategory('all'); }}>
                      Tout afficher
                    </button>
                  </div>
                ) : (
                  <ul className="home-decor__grid">
                    {shown.map((i, n) => {
                      const missing = i.price - state.money;
                      const poor = missing > 0;
                      return (
                        <li key={i.id} className={`home-decor__card${poor ? ' is-poor' : ''}`} style={{ ['--i' as any]: Math.min(n, 14) }}>
                          <div className="home-decor__tile">
                            <span className="home-decor__tile-glyph"><DecorGlyph name={i.name} category={i.category} /></span>
                          </div>
                          <div className="home-decor__card-body">
                            <span className="home-decor__card-name">{i.name}</span>
                            <span className="home-decor__card-meta">{categoryLabel(i.category)}</span>
                          </div>
                          <div className="home-decor__card-foot">
                            <span className="home-decor__price">
                              <span className="home-decor__price-icon"><Glyph id="coin" /></span>
                              {fmt(i.price)}
                            </span>
                            <button
                              type="button"
                              className="home-decor__button home-decor__button--primary"
                              disabled={poor || full}
                              title={poor ? `Il te manque ${fmt(missing)} Septims` : full ? 'Logement plein' : 'Voir le meuble devant toi'}
                              onClick={() => { setVisible(false); send({ action: 'buy', itemId: i.id }); }}
                            >
                              {poor ? `Il manque ${fmt(missing)}` : 'Placer'}
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </div>
          )}

          {tab === 'mine' && (
            <div className="home-decor__panel">
              <p className="home-decor__note">
                Un meuble rangé est repris à {state.resalePercent} % de son prix. Un coffre doit être vide pour être déplacé ou rangé.
                Si tu quittes le logement (fin de location, expulsion), les meubles y restent et sont perdus ; en cas de vente à un joueur, ils partent avec la maison.
              </p>
              {state.items.length === 0 ? (
                <div className="home-decor__empty">
                  <span className="home-decor__empty-icon"><Glyph id="chair" /></span>
                  <p>Ton logement est encore vide de meubles.</p>
                  <button type="button" className="home-decor__button home-decor__button--primary" onClick={() => setTab('shop')}>
                    Voir la boutique
                  </button>
                </div>
              ) : (
                <ul className="home-decor__rows">
                  {state.items.map((i, n) => (
                    <li key={i.refrId} className="home-decor__row" style={{ ['--i' as any]: Math.min(n, 14) }}>
                      <span className="home-decor__mini"><DecorGlyph name={i.name} category={i.category} /></span>
                      <div className="home-decor__row-main">
                        <span className="home-decor__row-name">{i.name}</span>
                        <span className="home-decor__row-meta">{categoryLabel(i.category)}, à {i.d} m</span>
                      </div>
                      <button type="button" className="home-decor__button" onClick={() => { setVisible(false); send({ action: 'moveItem', refrId: i.refrId }); }}>
                        Déplacer
                      </button>
                      <button type="button" className="home-decor__button home-decor__button--danger" onClick={() => send({ action: 'sellItem', refrId: i.refrId })}>
                        Ranger, +{fmt(i.sellFor)}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {tab === 'originals' && (
            <div className="home-decor__panel">
              <p className="home-decor__note">
                {state.canRemoveOriginals === false
                  ? "Ce que le commerce contenait au départ. Ces meubles restent en place, tu peux seulement les déplacer : c'est gratuit. Ajoute les tiens depuis la boutique. Les murs, sols et portes ne sont pas concernés."
                  : "Ce que la maison contenait au départ. Retire ou déplace ces objets pour les remplacer par les tiens : c'est gratuit, et tu peux tout remettre à tout moment. Les murs, sols et portes ne sont pas concernés."}
              </p>
              <div className="home-decor__actions">
                {state.canRemoveOriginals !== false && (
                  <button type="button" className="home-decor__button home-decor__button--danger" onClick={() => send({ action: 'hideAll' })}>
                    Tout retirer
                  </button>
                )}
                {marks.length > 0 && (
                  <button type="button" className="home-decor__button" onClick={() => send({ action: 'restoreAll' })}>
                    Tout remettre
                  </button>
                )}
                <button type="button" className="home-decor__button home-decor__button--quiet" onClick={() => send({ action: 'scan' })}>
                  Actualiser la liste
                </button>
              </div>

              <h3 className="home-decor__section">Autour de toi</h3>
              {nearby === null ? (
                <p className="home-decor__hint">Recherche des objets autour de toi...</p>
              ) : nearby.length === 0 ? (
                <p className="home-decor__hint">Aucun objet d'origine à portée.</p>
              ) : (
                <ul className="home-decor__rows">
                  {nearby.map((o, n) => (
                    <li key={o.refId} className={`home-decor__row${o.hidden ? ' is-off' : ''}`} style={{ ['--i' as any]: Math.min(n, 14) }}>
                      <span className="home-decor__mini"><DecorGlyph name={o.name} /></span>
                      <div className="home-decor__row-main">
                        <span className="home-decor__row-name">{o.name}</span>
                        <span className="home-decor__row-meta">à {o.d} m{o.hidden ? ', retiré' : o.moved ? ', déplacé' : ''}</span>
                      </div>
                      {o.hidden ? (
                        <button type="button" className="home-decor__button" onClick={() => send({ action: 'restoreOriginal', refId: o.refId })}>
                          Remettre
                        </button>
                      ) : (
                        <>
                          <button type="button" className="home-decor__button" onClick={() => { setVisible(false); send({ action: 'moveOriginal', refId: o.refId }); }}>
                            Déplacer
                          </button>
                          {state.canRemoveOriginals !== false && (
                            <button type="button" className="home-decor__button home-decor__button--danger" onClick={() => send({ action: 'hideOriginal', refId: o.refId })}>
                              Retirer
                            </button>
                          )}
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {marks.length > 0 && (
                <>
                  <h3 className="home-decor__section">Retirés ou déplacés</h3>
                  <ul className="home-decor__rows">
                    {marks.map((o) => (
                      <li key={`${o.kind}-${o.refId}`} className="home-decor__row">
                        <span className="home-decor__mini"><DecorGlyph name={o.name} /></span>
                        <div className="home-decor__row-main">
                          <span className="home-decor__row-name">{o.name}</span>
                          <span className="home-decor__row-meta">{o.kind}</span>
                        </div>
                        <button type="button" className="home-decor__button" onClick={() => send({ action: 'restoreOriginal', refId: o.refId })}>
                          Remettre
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>

        <footer className="home-decor__footer">
          <span className="home-decor__keyhint"><kbd>Échap</kbd> pour fermer</span>
          <button type="button" className="home-decor__button" onClick={close}>
            Fermer
          </button>
        </footer>
      </div>
    </div>
  );
};

export default HomeDecor;
